/**
 * 上传中心：模块级单例（通用上传引擎 + 弹层 UI）
 * - 本文件仅导出 useUploadCenter；其余能力通过 useUploadCenter() 返回的同一单例对象访问
 * - 业务侧通过 finalizeUpload 注入 OSS 成功后的处理
 */
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import store from '@/store';
import i18n from '@/plugins/i18n';

const t = i18n.global.t;

const UPLOAD_MAX_FILENAME_LEN = 80;
const UPLOAD_PROGRESS_CAP = 100;
const UPLOAD_MAX_CONCURRENT = 2;

const MAX_FILENAME_LEN = UPLOAD_MAX_FILENAME_LEN;
const PROGRESS_CAP = UPLOAD_PROGRESS_CAP;
const MAX_CONCURRENT = UPLOAD_MAX_CONCURRENT;

const panelVisible = ref(false);
const expanded = ref(true);

function setExpanded(val) {
  expanded.value = val;
}

function showUploadPanel() {
  panelVisible.value = true;
}

function hideUploadPanelOnly() {
  panelVisible.value = false;
}

/** @typedef {'oss' | 'backend'} UploadEngineMode */

const UPLOAD_MODE = {
  OSS: 'oss',
  BACKEND: 'backend'
};

let idSeq = 0;

function genUploadTaskId() {
  return `up-${Date.now()}-${++idSeq}`;
}

const tasks = ref([]);

const isUploading = computed(() => tasks.value.some((t) => t.status === 'pending' || t.status === 'uploading'));

/** 网络离线标志（响应式，供 pumpQueue 等逻辑判断） */
const isOffline = ref(typeof window !== 'undefined' ? !navigator.onLine : false);
/** 恢复网络后延迟确认计时器，防止 online 事件误触 */
let onlineConfirmTimer = null;
const ONLINE_CONFIRM_DELAY = 1500;

const aggregateProgress = computed(() => {
  const list = tasks.value.filter((t) => t.status === 'uploading' || t.status === 'pending');
  if (list.length === 0) return 0;
  let weighted = 0;
  let totalSize = 0;
  list.forEach((t) => {
    if (t.taskKind === 'folder') {
      const sz = Math.max(1, t.folderByteWeight || 1);
      weighted += (t.progress || 0) * sz;
      totalSize += sz;
      return;
    }
    const sz = t.file?.size > 0 ? t.file.size : 1;
    weighted += (t.progress || 0) * sz;
    totalSize += sz;
  });
  return totalSize > 0 ? Math.min(PROGRESS_CAP, Math.round(weighted / totalSize)) : 0;
});

let runningCount = 0;

function isCancelledError(error) {
  const code = error?.code;
  const codeNorm = typeof code === 'undefined' || code === null ? '' : String(code).toUpperCase();
  return (
    (typeof axios.isCancel === 'function' && axios.isCancel(error)) ||
    code === 'ERR_CANCELED' ||
    codeNorm === 'CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

function truncateFileName(fileName) {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0) {
    return fileName.length > MAX_FILENAME_LEN ? fileName.slice(0, MAX_FILENAME_LEN) : fileName;
  }
  const base = fileName.slice(0, lastDot);
  const ext = fileName.slice(lastDot);
  const maxBaseLen = MAX_FILENAME_LEN - ext.length;
  if (fileName.length > MAX_FILENAME_LEN) {
    return base.slice(0, maxBaseLen) + ext;
  }
  return fileName;
}

function notifyBatchTerminal(task) {
  const ctx = task.batchCtx;
  if (!ctx) return;
  ctx.done = (ctx.done || 0) + 1;
  if (task.status === 'completed') ctx.success = (ctx.success || 0) + 1;
  else if (task.status === 'failed') ctx.fail = (ctx.fail || 0) + 1;
  else if (task.status === 'cancelled') ctx.cancelled = (ctx.cancelled || 0) + 1;
  const total = ctx.total;
  if (typeof total === 'number' && ctx.done >= total) {
    ctx.onComplete?.(ctx, task);
  }
}

/** 用户单条取消成功后：提示并从列表移除；无任务时收起面板 */
function removeUploadTaskWithToast(taskId) {
  const idx = tasks.value.findIndex((t) => t.id === taskId);
  if (idx === -1) return;
  tasks.value.splice(idx, 1);
  ElMessage.success(t('cloudDocument.upload_center.cancel_success'));
  if (tasks.value.length === 0) {
    panelVisible.value = false;
  }
}

async function runFileTask(task) {
  const controller = new AbortController();
  task._abort = () => controller.abort();

  task.status = 'uploading';
  task.progress = 0;

  try {
    const res = await store.dispatch('uploadFileToAliyun', {
      options: {
        file: task.file,
        fileType: 'cloud-document',
        signal: controller.signal
      },
      onProgress: (p) => {
        if (!task._cancelRequested && task.status === 'uploading') {
          task.progress = p;
        }
      }
    });

    if (task._cancelRequested) {
      task.status = 'cancelled';
      task.progress = 0;
    } else if (typeof task.finalizeUpload !== 'function') {
      task.status = 'failed';
      task.errorMessage = 'finalizeUpload is required after OSS upload';
      task.progress = 0;
    } else {
      const finalizeResult = await task.finalizeUpload(task, {
        fileUrl: res.viewurl || '',
        signal: controller.signal
      });
      if (finalizeResult === false) {
        task.status = 'failed';
        task.errorMessage = task.errorMessage || 'Finalize upload failed';
        task.progress = 0;
      } else {
        task.status = 'completed';
        task.progress = PROGRESS_CAP;
      }
    }
  } catch (error) {
    if (task._offlineAbort || isOffline.value) {
      // 断网自动中止：暂停任务，等待网络恢复后自动续传
      task.status = 'paused';
      task.progress = 0;
    } else if (task._cancelRequested || isCancelledError(error)) {
      task.status = 'cancelled';
      task.progress = 0;
    } else {
      task.status = 'failed';
      task.errorMessage = error?.message || String(error);
      task.progress = 0;
    }
  } finally {
    task._abort = null;
    runningCount--;
    pumpQueue();
    // paused 任务等待恢复后再通知批量上下文，避免 onComplete 提前触发
    if (task.status !== 'paused') {
      notifyBatchTerminal(task);
    }
    if (task.status === 'cancelled' && task._userCancelledUi) {
      task._userCancelledUi = false;
      removeUploadTaskWithToast(task.id);
    }
  }
}

function createFileTask(payload) {
  return reactive({
    id: genUploadTaskId(),
    taskKind: 'file',
    fileName: payload.file.name,
    file: payload.file,
    documentId: '',
    rootId: payload.rootId,
    status: 'pending',
    progress: 0,
    errorMessage: '',
    batchCtx: payload.batchCtx || null,
    uploadMode: payload.uploadMode || UPLOAD_MODE.OSS,
    finalizeUpload: payload.finalizeUpload,
    _abort: null,
    _cancelRequested: false,
    _offlineAbort: false,
    _userCancelledUi: false
  });
}

function pumpQueue() {
  if (isOffline.value) return;
  while (runningCount < MAX_CONCURRENT) {
    const next = tasks.value.find((t) => t.taskKind === 'file' && t.status === 'pending');
    if (!next) break;
    runningCount++;
    void runFileTask(next).catch((err) => {
      if (!isCancelledError(err)) {
        console.warn('[useUploadCenter] runFileTask rejection', err);
      }
    });
  }
}

function enqueueFile(payload) {
  if (payload.uploadMode === UPLOAD_MODE.BACKEND) {
    console.warn('[uploadEngine] backend 模式尚未实现，已回退为 oss');
  }
  const task = createFileTask({ ...payload, uploadMode: UPLOAD_MODE.OSS });
  tasks.value.unshift(task);
  showUploadPanel();
  pumpQueue();
  return task.id;
}

function uploadFiles(fileList, options) {
  const { documentId, rootId, onComplete, finalizeUpload } = options;
  const ids = [];
  const batchCtx = {
    total: fileList.length,
    done: 0,
    success: 0,
    fail: 0,
    cancelled: 0,
    onComplete
  };
  fileList.forEach((file) => {
    const id = enqueueFile({
      file,
      documentId,
      rootId,
      batchCtx,
      finalizeUpload
    });
    ids.push(id);
  });
  return ids;
}

/**
 * 文件夹上传：单文件一轮 OSS + 可选 finalize（降低 uploadFolder 嵌套深度）
 * @returns {Promise<boolean>} 是否继续后续文件
 */
async function runFolderSingleFileStep(folderTask, file, files, uploadedCountRef, controller) {
  const res = await store.dispatch('uploadFileToAliyun', {
    options: {
      file,
      fileType: 'cloud-document',
      signal: controller.signal
    },
    onProgress: (p) => {
      const base = (uploadedCountRef.n / Math.max(files.length, 1)) * PROGRESS_CAP;
      const slice = (p / PROGRESS_CAP) * (1 / Math.max(files.length, 1)) * PROGRESS_CAP;
      folderTask.progress = Math.min(PROGRESS_CAP - 1, Math.round(base + slice));
    }
  });

  const uploadedFileUrl = res.viewurl || '';
  if (!uploadedFileUrl) {
    throw new Error('OSS upload returned empty URL');
  }
  uploadedCountRef.n += 1;
  folderTask.folderDone = (folderTask._folderDoneOffset || 0) + uploadedCountRef.n;
  folderTask.progress = Math.round((uploadedCountRef.n / Math.max(files.length, 1)) * PROGRESS_CAP);

  if (typeof folderTask.finalizeUpload !== 'function') {
    return true;
  }
  await folderTask.finalizeUpload(folderTask, {
    file,
    fileUrl: uploadedFileUrl,
    done: uploadedCountRef.n,
    total: files.length,
    signal: controller.signal
  });
  return true;
}

/**
 * 文件夹上传核心循环（初次 + 重试复用）
 * 网络中断时将剩余文件保存至 folderTask._remainingFiles，以便重试继续。
 */
async function runFolderUploadLoop(folderTask, files) {
  const controller = new AbortController();
  folderTask._abort = () => controller.abort();
  const uploadedCountRef = { n: 0 };

  try {
    for (let i = 0; i < files.length; i++) {
      if (controller.signal.aborted || folderTask._cancelRequested) break;
      // File.webkitRelativePath 为只读，禁止赋值；webkitdirectory 选中的文件自带该路径
      const continueFolder = await runFolderSingleFileStep(folderTask, files[i], files, uploadedCountRef, controller);
      if (!continueFolder) break;
    }

    if (folderTask._offlineAbort || isOffline.value) {
      // 断网：保存剩余文件，暂停任务，网络恢复后自动续传
      folderTask._remainingFiles = files.slice(uploadedCountRef.n);
      folderTask.status = 'paused';
      folderTask.progress = 0;
      return;
    }
    if (folderTask._cancelRequested || controller.signal.aborted) {
      folderTask.status = 'cancelled';
      folderTask.progress = 0;
      folderTask._onComplete?.({ success: 0, fail: 0, cancelled: 1, done: 1, total: 1 }, folderTask);
      return;
    }

    if (typeof folderTask.finalizeUpload === 'function') {
      const finalizeResult = await folderTask.finalizeUpload(folderTask, {
        done: true,
        signal: controller.signal
      });
      if (finalizeResult === false) {
        folderTask.status = 'failed';
        return;
      }
    }

    folderTask.status = 'completed';
    folderTask.progress = PROGRESS_CAP;
    folderTask._remainingFiles = null;
    folderTask._onComplete?.({ success: 1, fail: 0, cancelled: 0, done: 1, total: 1 }, folderTask);
  } catch (error) {
    if (folderTask._offlineAbort || isOffline.value) {
      // 断网：保存剩余文件，暂停任务，网络恢复后自动续传
      folderTask._remainingFiles = files.slice(uploadedCountRef.n);
      folderTask.status = 'paused';
      folderTask.progress = 0;
    } else if (folderTask._cancelRequested || isCancelledError(error) || controller.signal.aborted) {
      folderTask.status = 'cancelled';
      folderTask.progress = 0;
      folderTask._onComplete?.({ success: 0, fail: 0, cancelled: 1, done: 1, total: 1 }, folderTask);
    } else {
      // 网络中断：保存剩余文件供重试，已成功的 OSS 数据保留在业务层，重试时一并落库
      folderTask._remainingFiles = files.slice(uploadedCountRef.n);
      folderTask.status = 'failed';
      folderTask.errorMessage = error?.message || String(error);
      folderTask.progress = 0;
    }
  } finally {
    folderTask._abort = null;
    if (folderTask.status === 'cancelled' && folderTask._userCancelledUi) {
      folderTask._userCancelledUi = false;
      removeUploadTaskWithToast(folderTask.id);
    }
  }
}

async function uploadFolder(rawFiles, options) {
  const { onComplete, finalizeUpload, onRetryStart } = options;
  const files = Array.from(rawFiles).filter((f) => f.type !== 'image/svg+xml');

  if (files.length === 0) {
    onComplete?.({ success: 0, fail: 0, cancelled: 0, done: 0, total: 0 }, null);
    return;
  }

  const rootFolderName = files[0].webkitRelativePath.split('/')[0];
  const folderTask = reactive({
    id: genUploadTaskId(),
    taskKind: 'folder',
    fileName: rootFolderName,
    documentId: '',
    rootId: options.rootId,
    status: 'uploading',
    progress: 0,
    folderDone: 0,
    folderTotal: files.length,
    folderByteWeight: files.reduce((s, f) => s + (f.size || 0), 0) || 1,
    _originalFolderTotal: files.length,
    _folderDoneOffset: 0,
    errorMessage: '',
    batchCtx: null,
    _abort: null,
    _cancelRequested: false,
    _offlineAbort: false,
    file: null,
    finalizeUpload: finalizeUpload || null,
    _onComplete: onComplete || null,
    _onRetryStart: onRetryStart || null,
    _remainingFiles: null,
    _userCancelledUi: false
  });

  tasks.value.unshift(folderTask);
  showUploadPanel();

  void runFolderUploadLoop(folderTask, files).catch((err) => {
    if (!isCancelledError(err)) {
      console.warn('[useUploadCenter] uploadFolder error', err);
    }
  });
}

function cancelTask(taskId) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) return;
  if (task.status === 'pending' || task.status === 'paused') {
    task._cancelRequested = true;
    task._offlineAbort = false;
    task.status = 'cancelled';
    task.progress = 0;
    notifyBatchTerminal(task);
    removeUploadTaskWithToast(taskId);
    return;
  }
  if (task.status === 'uploading') {
    task._userCancelledUi = true;
    task._cancelRequested = true;
    task._abort?.();
  }
}

function cancelAll() {
  tasks.value.forEach((task) => {
    if (task.status === 'uploading' || task.status === 'pending') {
      const wasPending = task.status === 'pending';
      task._cancelRequested = true;
      task._abort?.();
      if (wasPending) {
        task.status = 'cancelled';
        notifyBatchTerminal(task);
      }
    } else if (task.status === 'paused') {
      task._cancelRequested = true;
      task._offlineAbort = false;
      task.status = 'cancelled';
      notifyBatchTerminal(task);
    }
  });
}

function clearAll() {
  cancelAll();
  tasks.value = [];
  panelVisible.value = false;
  expanded.value = true;
}

function hasRunningTask() {
  return tasks.value.some((t) => t.status === 'uploading' || t.status === 'pending' || t.status === 'paused');
}

function retryTask(taskId) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) return;

  // ── 文件夹重试 ──
  // _remainingFiles 由 runFolderUploadLoop 在网络中断时写入，仅含尚未上传的文件
  // paused 状态由 handleOnline 自动续传，不允许手动重试
  if (task.taskKind === 'folder') {
    if (task.status !== 'failed') return;
    const remaining = task._remainingFiles;
    if (!remaining?.length) return;
    task._remainingFiles = null;
    // 通知业务层为剩余文件重建目录树（data.folderList 已被 done:true 落库后清空）
    task._onRetryStart?.(task, remaining);
    task.status = 'uploading';
    task.progress = 0;
    task.errorMessage = '';
    const retryDoneAlready = (task._originalFolderTotal || remaining.length) - remaining.length;
    task._folderDoneOffset = retryDoneAlready;
    task.folderDone = retryDoneAlready;
    task.folderTotal = task._originalFolderTotal || remaining.length;
    task._cancelRequested = false;
    task._offlineAbort = false;
    task._userCancelledUi = false;
    showUploadPanel();
    void runFolderUploadLoop(task, remaining).catch((err) => {
      if (!isCancelledError(err)) console.warn('[useUploadCenter] folder retry error', err);
    });
    return;
  }

  // ── 文件重试 ──
  // 用户主动取消的任务会被 removeUploadTaskWithToast 从列表移除，
  // 列表中残留的 cancelled 任务均为网络中断所致，允许重试。
  const isRetryable = (task.status === 'failed' || task.status === 'cancelled') && task.file;
  if (!isRetryable) return;
  task.batchCtx = null;
  task.status = 'pending';
  task.progress = 0;
  task.errorMessage = '';
  task._cancelRequested = false;
  task._offlineAbort = false;
  task._userCancelledUi = false;
  showUploadPanel();
  pumpQueue();
}

function toggleExpand() {
  setExpanded(!expanded.value);
}

/**
 * 断网处理：中止所有活跃上传，将任务置为暂停。
 */
function handleOffline() {
  if (isOffline.value) return;
  isOffline.value = true;
  if (onlineConfirmTimer) {
    clearTimeout(onlineConfirmTimer);
    onlineConfirmTimer = null;
  }
  tasks.value.forEach((task) => {
    if (task.status === 'uploading') {
      task._offlineAbort = true;
      task._cancelRequested = true; // 阻止进度更新
      task._abort?.();
    } else if (task.status === 'pending') {
      task._offlineAbort = true;
      task.status = 'paused';
    }
  });
}

/**
 * 恢复网络处理（延迟 1.5s 确认）：重新入队 file 任务，续传 folder 任务。
 */
function handleOnline() {
  if (onlineConfirmTimer) clearTimeout(onlineConfirmTimer);
  onlineConfirmTimer = setTimeout(() => {
    onlineConfirmTimer = null;
    if (!isOffline.value) return;
    isOffline.value = false;

    // 恢复文件任务
    tasks.value.forEach((task) => {
      if (task.taskKind !== 'file' || !task._offlineAbort) return;
      task._offlineAbort = false;
      task._cancelRequested = false;
      task.status = 'pending';
      task.progress = 0;
      task.errorMessage = '';
    });
    pumpQueue();

    // 续传文件夹任务
    tasks.value.forEach((task) => {
      if (task.taskKind !== 'folder' || !task._offlineAbort) return;
      const remaining = task._remainingFiles;
      if (!remaining?.length) return;
      task._offlineAbort = false;
      task._cancelRequested = false;
      task._remainingFiles = null;
      // data.folderList 保留了断网前已成功 OSS 文件的 fileUrl，无需重建
      task._onRetryStart?.(task, remaining);
      task.status = 'uploading';
      task.progress = 0;
      task.errorMessage = '';
      const resumeDoneAlready = (task._originalFolderTotal || remaining.length) - remaining.length;
      task._folderDoneOffset = resumeDoneAlready;
      task.folderDone = resumeDoneAlready;
      task.folderTotal = task._originalFolderTotal || remaining.length;
      void runFolderUploadLoop(task, remaining).catch((err) => {
        if (!isCancelledError(err)) console.warn('[useUploadCenter] folder resume error', err);
      });
    });
  }, ONLINE_CONFIRM_DELAY);
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
}

/** 模块单例 API：每次调用 useUploadCenter() 均返回同一引用 */
const uploadCenterApi = {
  panelVisible,
  expanded,
  setExpanded,
  toggleExpand,
  showPanel: showUploadPanel,
  hidePanelOnly: hideUploadPanelOnly,
  tasks,
  isUploading,
  aggregateProgress,
  UPLOAD_MODE,
  UPLOAD_MAX_FILENAME_LEN,
  UPLOAD_PROGRESS_CAP,
  UPLOAD_MAX_CONCURRENT,
  genUploadTaskId,
  createFileTask,
  pumpQueue,
  runFileTask,
  notifyBatchTerminal,
  isCancelledError,
  enqueueFile,
  uploadFiles,
  uploadFolder,
  cancelTask,
  cancelAll,
  clearAll,
  retryTask,
  hasRunningTask,
  truncateFileName,
  isOffline
};

/**
 * 上传中心唯一导出：返回模块级单例 API（弹层 + 引擎）
 * @returns {typeof uploadCenterApi}
 */
export function useUploadCenter() {
  return uploadCenterApi;
}
